import React, { Dispatch, MouseEvent, useRef } from 'react';
import '../App.css';
import { useDrag, useDrop, DragSourceMonitor } from 'react-dnd'
import { ItemTypes } from '../ItemTypes'
import { IInject, IInjectNode, ICOA, IRFI, IRoleContent } from '../interfaces';
import COADots from './COADots';
import type { Identifier, XYCoord } from 'dnd-core'
import ContentEditable from 'react-contenteditable'

interface VoteProps {
    id: number;
    index: number;
    text: string;
    nodeId: string;
    moveVote: (dragIndex: number, hoverIndex: number) => void
    setInjectItem: Dispatch<any>;
    injectItem: IInject;
    isCOAEditing: string;
    isRFIEditing: string;
    isInfoEditing: string;
    voteType: string;
    injectComponents: IInject[];
    setInjectComponents: Dispatch<any>;
    onChangeSave: Dispatch<any>;
    turn: number;
    coa: ICOA;
    coaOpen: string;
    setIsCOADragging: Dispatch<boolean>;
    isCOADragging: boolean;
    nodeHeight: string;
    setCoaClicked: Dispatch<boolean>;
    coaClicked: boolean;
    saveScenario: (i: IInject[]) => Promise<void>;
    onFocus: () => void;
    onBlur: () => void;
  }

  interface DragItem {
    index: number
    id: string
    type: string
  }

const Vote: React.FC<VoteProps> = ({
  id, index, text, nodeId, injectItem, voteType, injectComponents, turn, coa,
  isCOAEditing, isRFIEditing, isInfoEditing, coaOpen, nodeHeight, isCOADragging, coaClicked,
  moveVote, setInjectItem, setInjectComponents, onChangeSave, saveScenario,
  setIsCOADragging, setCoaClicked, onFocus, onBlur,
}) => {
  
  let ref = useRef<any>(null)

  const contentEditableRef = useRef<any>()

  const onChangeVotes = (ev: React.FormEvent<HTMLInputElement>) => {
    onChangeSave({})
    let newNodes: IInjectNode[] = [];
    newNodes = JSON.parse(JSON.stringify(injectItem.nodes));
    newNodes.forEach((item) => {
      if (nodeId.toString() === item.id) {
        if (voteType === 'coa') {
          item.coas.forEach((item1: ICOA, i: number) => {
            if (i === index) {
              item1.displayText = ev.currentTarget.innerText
            }
          })
        }
        if (voteType === 'rfi') {
          item.rfis.forEach((item1: IRFI, i: number) => {
            if (i === index) {
              item1.displayText = ev.currentTarget.innerText
            }
          })
        }
        if (voteType === 'info') {
          item.informationBriefs.forEach((item1: IRoleContent, i: number) => {
            if (i === index) {
              item1.displayText = ev.currentTarget.innerText
            }
          })
        }
      }
    })

    setInjectItem({
      ...injectItem,
      nodes: newNodes
    });

    setInjectComponents(injectComponents.map((inject: IInject) => {
      if (inject.id === injectItem.id) {
          return { ...inject, nodes: newNodes };
      } else {
          return inject;
      }
    }));

  }

  const trashVote = (ev: MouseEvent) => {
    ev.stopPropagation();
    let newNodes: IInjectNode[] = [];
    let filteredVotesCOA: ICOA[] = [];
    let filteredVotesRFI: IRFI[] = [];
    newNodes = JSON.parse(JSON.stringify(injectItem.nodes));
    newNodes.forEach((item) => {
      if (nodeId.toString() === item.id) {
        if (voteType === 'coa') {
          item.coas.forEach((item1: ICOA, i: number) => {
            if (i === index) {
                filteredVotesCOA = item.coas.filter((data: ICOA, k: number) => index !== k )
                item.coas = filteredVotesCOA;
            }
          })
        }
        if (voteType === 'rfi') {
          item.rfis.forEach((item1: IRFI, i: number) => {
            if (i === index) {
                filteredVotesRFI = item.rfis.filter((data: IRFI, k: number) => index !== k )
                item.rfis = filteredVotesRFI;
            }
          })
        }
      }
    })

    setInjectItem({
      ...injectItem,
      nodes: newNodes
    });

    setInjectComponents(injectComponents.map((inject: IInject) => {
      if (inject.id === injectItem.id) {
          return { ...inject, nodes: newNodes };
      } else {
          return inject;
      }
    }));
  }

  const [{ handlerId }, drop] = useDrop<
    DragItem,
    void,
    { handlerId: Identifier | null }
  >({
    accept: ItemTypes.VOTE,
    collect(monitor) {
      return {
        handlerId: monitor.getHandlerId(),
      }
    },
    hover(item: DragItem, monitor) {
      if (!ref.current) {
        return
      }

      const dragIndex = item.index
      const hoverIndex = index

      if (dragIndex === hoverIndex) {
        return
      }

      const hoverBoundingRect = ref.current?.getBoundingClientRect()

      const hoverMiddleY =
        (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2

      const clientOffset = monitor.getClientOffset()

      const hoverClientY = (clientOffset as XYCoord).y - hoverBoundingRect.top

      if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) {
        return
      }

      if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) {
        return
      }

      moveVote(dragIndex, hoverIndex)

      item.index = hoverIndex
    },
  })

  const [{ isDragging }, drag] = useDrag({
    type: ItemTypes.VOTE,
    item: () => {
      return { id, index }
    },
    canDrag: () => {
      return coaClicked
    },
    collect: (monitor: DragSourceMonitor) => ({
      isDragging: monitor.isDragging(),
    }),
  }, [coaClicked])

  const opacity = isDragging ? 0 : 1
  drag(drop(ref))

  return (
    <>
    { voteType === 'coa' ? 
      <div ref={ref} style={{ opacity, position: coaOpen === 'closed' ? 'absolute' : 'relative' }} className={`coa-vote ${isCOAEditing} ${coaOpen === 'closed' ? 'coa-hidden' : ''}`} data-voteid={id} data-handler-id={handlerId} key={index}>
          <COADots coaId={id} srcNodeId={nodeId} setCoaClicked={setCoaClicked} coaClicked={coaClicked} setInjectItem={setInjectItem} injectItem={injectItem} injectComponents={injectComponents} setInjectComponents={setInjectComponents} turn={turn} coa={coa} saveScenario={saveScenario} setIsCOADragging={setIsCOADragging} isCOADragging={isCOADragging} nodeHeight={nodeHeight} />
          <div className="vote-number">{index+1}</div>
          <ContentEditable
              className="coa-vote-desc"
              data-placeholder="COA text here..."
              data-nodeid={nodeId}
              data-coaid={id}
              ref={contentEditableRef}
              html={text} // innerHTML of the editable div
              disabled={false}       // use true to disable editing
              onChange={onChangeVotes} // handle innerHTML change
              onFocus={onFocus}
              onBlur={onBlur}
          />
          <button className={`vote-trash ${isCOAEditing}`} onClick={trashVote} data-voteid={id}>
              <img src="img/trash-can.svg" alt="trash can" className="vote-trash-svg" />
          </button>
          { injectItem.nodes.length === 1 ? <div className="node-dot-off"></div> : <></> }
      </div> : <></> }

      { voteType === 'rfi' ? 
      <div ref={ref} style={{ opacity }} className={`rfi-vote ${isRFIEditing}`} data-voteid={id} data-handler-id={handlerId} key={index}>
          <div className="vote-number">{index+1}</div>
          <ContentEditable
              className="vote-desc"
              data-placeholder="RFI text here..."
              ref={contentEditableRef}
              data-nodeid={nodeId}
              html={text} // innerHTML of the editable div
              disabled={false}       // use true to disable editing
              onChange={onChangeVotes} // handle innerHTML change
              onFocus={onFocus}
              onBlur={onBlur}
          />
          <button className={`vote-trash ${isRFIEditing}`} onClick={trashVote} data-voteid={id}>
              <img src="img/trash-can.svg" alt="trash can" className="vote-trash-svg" />
          </button>
          { injectItem.nodes.length === 1 ? <div className="node-dot-off"></div> : <></> }
      </div> : <></> }

      { voteType === 'info' ?
      <div ref={ref} style={{ opacity }} className={`info-vote ${isInfoEditing}`} data-voteid={id} data-handler-id={handlerId} key={index}>
          <div className="vote-number">{index+1}</div>
          <ContentEditable
              className="info-vote-desc"
              data-placeholder="Information Brief text here..."
              ref={contentEditableRef}
              data-nodeid={nodeId}
              html={text} // innerHTML of the editable div
              disabled={false}       // use true to disable editing
              onChange={onChangeVotes} // handle innerHTML change
              onFocus={onFocus}
              onBlur={onBlur}
          />
          <button className={`vote-trash ${isInfoEditing}`} onClick={trashVote} data-voteid={id}>
              <img src="img/trash-can.svg" className="vote-trash-svg" alt="trash can" />
          </button>
          { injectItem.nodes.length === 1 ? <div className="node-dot-off"></div> : <></> }
      </div> : <></> }
      
    </>
  )
}

export default Vote;